/**
 * Site Overview Component
 *
 * Displays an overview of site properties including:
 * - Number of fixed bays with ID ranges
 * - Number of super bays with their names
 * - Tournament range status
 * - Enabled/disabled status
 * - Shot latency
 * - Reliability assessment (site-level hint and per-bay hints)
 */

import { useEffect } from 'react';
import { useSite } from '../context/useSite';
import { useAnalytics } from '../../analytics';
import { ShotLatencyCard } from './ShotLatencyCard';
import { SiteReliabilityCard } from './reliability/SiteReliabilityCard';
import './SiteOverview.css';

/**
 * Parses bay IDs and groups them into ranges.
 * If IDs are parseable as integers, groups consecutive numbers into ranges (e.g., "2-4, 80-122").
 * Non-numeric IDs are displayed as-is.
 */
function formatBayRanges(bayIds: readonly string[]): string {
  if (bayIds.length === 0) return 'None';

  const numericIds: number[] = [];
  const nonNumericIds: string[] = [];

  for (const id of bayIds) {
    const parsed = parseInt(id, 10);
    if (!isNaN(parsed) && String(parsed) === id.trim()) {
      numericIds.push(parsed);
    } else {
      nonNumericIds.push(id);
    }
  }

  const ranges: string[] = [];

  if (numericIds.length > 0) {
    numericIds.sort((a, b) => a - b);

    let rangeStart = numericIds[0];
    let rangeEnd = numericIds[0];

    for (let i = 1; i < numericIds.length; i++) {
      if (numericIds[i] === rangeEnd + 1) {
        rangeEnd = numericIds[i];
      } else {
        ranges.push(rangeStart === rangeEnd ? String(rangeStart) : `${rangeStart}-${rangeEnd}`);
        rangeStart = numericIds[i];
        rangeEnd = numericIds[i];
      }
    }
    ranges.push(rangeStart === rangeEnd ? String(rangeStart) : `${rangeStart}-${rangeEnd}`);
  }

  ranges.push(...nonNumericIds);

  return ranges.join(', ');
}

export function SiteOverview(): JSX.Element {
  const { site } = useSite();
  const { track } = useAnalytics();

  // Track page view when site is available
  // Requirement 5.3: Track "Pocket Admin Site Overview Viewed" with SiteId
  useEffect(() => {
    if (site) {
      track('Pocket Admin Site Overview Viewed', { SiteId: site.siteId });
    }
  }, [site, track]);

  if (!site) {
    return <div className="site-overview__empty">No site loaded</div>;
  }

  const fixedBayIds = site.bays.fixedBays.map((bay) => bay.id);
  const superBayNames = site.bays.superBays.map((bay) => bay.name);
  const hasBays = site.bays.fixedBays.length > 0 || site.bays.superBays.length > 0;

  return (
    <div className="site-overview">
      <h2 className="site-overview__title">Site Overview</h2>

      <div className="site-overview__grid">
        <div className="site-overview__card">
          <h3 className="site-overview__card-title">Go Fixed Bays</h3>
          <p className="site-overview__card-value">{site.bays.fixedBays.length}</p>
          <p className="site-overview__card-detail">
            <span className="site-overview__label">Bay IDs:</span> {formatBayRanges(fixedBayIds)}
          </p>
        </div>

        <div className="site-overview__card">
          <h3 className="site-overview__card-title">Go Super Bays</h3>
          <p className="site-overview__card-value">{site.bays.superBays.length}</p>
          {superBayNames.length > 0 && (
            <p className="site-overview__card-detail">{superBayNames.join(', ')}
            </p>
          )}
        </div>

        <div className="site-overview__card">
          <h3 className="site-overview__card-title">Type</h3>
          <p className="site-overview__card-value">
            {site.isTournamentRange ? 'Tour' : 'Range'}
          </p>
        </div>

        <div className="site-overview__card">
          <h3 className="site-overview__card-title">Status</h3>
          <p className={`site-overview__card-value site-overview__card-value--${site.isDisabled || site.isVirtual || hasBays === false ? 'disabled' : 'enabled'}`}>
            {site.isDisabled || site.isVirtual || hasBays === false ? 'Disabled' : 'Enabled'}
          </p>
        </div>

        <ShotLatencyCard siteId={site.siteId} />
      </div>

      <SiteReliabilityCard siteId={site.siteId} superBays={site.bays.superBays} />
    </div>
  );
}
